import React, { useRef, useState } from "react";
import { Phone, MessageSquareText } from "lucide-react";
import { buzz } from "@/lib/mobile";

const THRESHOLD = 76;   // px per attivare l'azione
const MAX_DRAG = 112;   // limite trascinamento

/**
 * Wrapper swipe per card mobile:
 *  - swipe DESTRA  → onSwipeRight (Chiama, sfondo verde)
 *  - swipe SINISTRA → onSwipeLeft (Messaggio, sfondo sky)
 * Non interferisce con lo scroll verticale (touch-action: pan-y, lock direzionale).
 */
export default function SwipeActions({ onSwipeRight, onSwipeLeft, disabled = false, radius = 12, children, testid }) {
  const [dx, setDx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const startRef = useRef(null);   // {x, y, lock}
  const draggedRef = useRef(false);
  const crossedRef = useRef(false);

  if (disabled) return children;

  const onTouchStart = (e) => {
    const t = e.touches[0];
    startRef.current = { x: t.clientX, y: t.clientY, lock: null };
    crossedRef.current = false;
    setAnimating(false);
  };

  const onTouchMove = (e) => {
    const s = startRef.current;
    if (!s) return;
    const t = e.touches[0];
    const ddx = t.clientX - s.x;
    const ddy = t.clientY - s.y;
    if (s.lock === null) {
      if (Math.abs(ddx) > 12 && Math.abs(ddx) > Math.abs(ddy) * 1.4) s.lock = "h";
      else if (Math.abs(ddy) > 12) s.lock = "v";
    }
    if (s.lock !== "h") return;
    draggedRef.current = true;
    const clamped = Math.max(-MAX_DRAG, Math.min(MAX_DRAG, ddx));
    setDx(clamped);
    const past = Math.abs(clamped) >= THRESHOLD;
    if (past && !crossedRef.current) { crossedRef.current = true; buzz(16); }
    else if (!past) crossedRef.current = false;
  };

  const onTouchEnd = () => {
    const s = startRef.current;
    if (s?.lock === "h") {
      if (dx >= THRESHOLD && onSwipeRight) { buzz(); onSwipeRight(); }
      else if (dx <= -THRESHOLD && onSwipeLeft) { buzz(); onSwipeLeft(); }
    }
    setAnimating(true);
    setDx(0);
    startRef.current = null;
    setTimeout(() => { draggedRef.current = false; }, 60);
  };

  const onClickCapture = (e) => {
    if (draggedRef.current) { e.preventDefault(); e.stopPropagation(); }
  };

  const progress = Math.min(Math.abs(dx) / THRESHOLD, 1);
  const right = dx > 0; // rivela zona Chiama
  const past = progress >= 1;

  return (
    <div className="relative" style={{ borderRadius: radius, overflow: "hidden" }} data-testid={testid}>
      {/* Sfondo azione */}
      {dx !== 0 && (
        <div
          className={`absolute inset-0 flex items-center ${right ? "justify-start" : "justify-end"} px-5`}
          style={{
            background: right
              ? "linear-gradient(90deg, #16A34A, #22C55E)"
              : "linear-gradient(270deg, #0284C7, #0EA5E9)",
            opacity: 0.25 + progress * 0.75,
          }}
        >
          <div
            className="flex items-center gap-2 text-white font-bold text-[13px]"
            style={{ transform: `scale(${past ? 1.15 : 0.85 + progress * 0.25})`, transition: "transform 120ms ease" }}
          >
            {right ? (
              <><Phone className="w-5 h-5" strokeWidth={2.4} /> Chiama</>
            ) : (
              <>Messaggio <MessageSquareText className="w-5 h-5" strokeWidth={2.4} /></>
            )}
          </div>
        </div>
      )}
      {/* Layer trascinabile */}
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        onClickCapture={onClickCapture}
        style={{
          transform: `translateX(${dx}px)`,
          transition: animating ? "transform 260ms cubic-bezier(0.2, 0.8, 0.3, 1.05)" : "none",
          touchAction: "pan-y",
        }}
      >
        {children}
      </div>
    </div>
  );
}
